import React, { useState, useEffect } from 'react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiMapPin,
  FiX,
  FiUpload
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdminLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const data = await api.getLocations();
      setLocations(data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingLocation) {
        await api.updateLocation(editingLocation._id, formData);
        setLocations(locations.map(l =>
          l._id === editingLocation._id ? { ...l, ...formData } : l
        ));
        toast.success('Location updated');
      } else {
        const data = await api.createLocation(formData);
        setLocations([...locations, data.location]);
        toast.success('Location created');
      }
      resetForm();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      description: location.description || '',
      image: location.image || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will affect all apartments in this location.')) return;

    try {
      await api.deleteLocation(id);
      setLocations(locations.filter(l => l._id !== id));
      toast.success('Location deleted');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingLocation(null);
    setFormData({ name: '', description: '', image: '' });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('image', file);

    try {
      const data = await api.uploadLocationImage(formDataUpload);
      setFormData(prev => ({ ...prev, image: data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    addBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      background: '#1a3a5c',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    cardImage: {
      width: '100%',
      height: '160px',
      objectFit: 'cover',
      background: '#f5f5f5'
    },
    cardBody: {
      padding: '20px'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '8px'
    },
    cardName: {
      fontWeight: '600',
      fontSize: '1.125rem'
    },
    cardActions: {
      display: 'flex',
      gap: '8px'
    },
    iconBtn: {
      padding: '6px',
      background: 'none',
      border: '1px solid #ddd',
      borderRadius: '6px',
      cursor: 'pointer'
    },
    cardDescription: {
      color: '#555',
      fontSize: '0.875rem',
      marginBottom: '12px'
    },
    cardMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      color: '#555',
      fontSize: '0.8125rem'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '24px'
    },
    modalContent: {
      background: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      borderBottom: '1px solid #eee'
    },
    modalTitle: {
      fontSize: '1.25rem',
      fontWeight: '600'
    },
    closeBtn: {
      padding: '8px',
      background: 'none',
      border: 'none',
      cursor: 'pointer'
    },
    modalBody: {
      padding: '24px'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontWeight: '500',
      marginBottom: '8px',
      fontSize: '0.875rem'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem',
      minHeight: '100px',
      resize: 'vertical'
    },
    imageUpload: {
      border: '2px dashed #ddd',
      borderRadius: '8px',
      padding: '20px',
      textAlign: 'center',
      cursor: 'pointer'
    },
    imagePreview: {
      marginTop: '12px',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    previewImg: {
      width: '100%',
      height: '120px',
      objectFit: 'cover'
    },
    modalFooter: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      padding: '20px 24px',
      borderTop: '1px solid #eee'
    },
    btn: {
      padding: '10px 20px',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    btnPrimary: {
      background: '#1a3a5c',
      color: 'white',
      border: 'none'
    },
    btnSecondary: {
      background: 'white',
      color: '#222',
      border: '1px solid #ddd'
    },
    empty: {
      textAlign: 'center',
      padding: '60px',
      background: 'white',
      borderRadius: '12px',
      color: '#555'
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <header style={styles.header}>
        <h1 style={styles.title}>Locations</h1>
        <button style={styles.addBtn} onClick={() => setShowForm(true)}>
          <FiPlus /> Add Location
        </button>
      </header>

      {locations.length > 0 ? (
        <div style={styles.grid}>
          {locations.map(location => (
            <div key={location._id} style={styles.card}>
              <img
                src={location.image || '/images/placeholder-location.jpg'}
                alt={location.name}
                style={styles.cardImage}
              />
              <div style={styles.cardBody}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardName}>{location.name}</h3>
                  <div style={styles.cardActions}>
                    <button
                      style={styles.iconBtn}
                      onClick={() => handleEdit(location)}
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      style={{ ...styles.iconBtn, color: '#C62828' }}
                      onClick={() => handleDelete(location._id)}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
                {location.description && (
                  <p style={styles.cardDescription}>
                    {location.description.substring(0, 100)}...
                  </p>
                )}
                <div style={styles.cardMeta}>
                  <FiMapPin size={14} />
                  {location.apartmentCount || 0} apartments
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.empty}>
          <FiMapPin size={48} style={{ marginBottom: '16px', color: '#ddd' }} />
          <h3 style={{ marginBottom: '8px' }}>No locations yet</h3>
          <p>Add your first location to get started</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={styles.modal} onClick={resetForm}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </h2>
              <button style={styles.closeBtn} onClick={resetForm}>
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={styles.modalBody}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Location Name *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Central London"
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Description</label>
                  <textarea
                    style={styles.textarea}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe this location..."
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Image</label>
                  <label style={styles.imageUpload}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <FiUpload size={20} style={{ marginBottom: '8px' }} />
                    <div>Click to upload image</div>
                  </label>
                  {formData.image && (
                    <div style={styles.imagePreview}>
                      <img src={formData.image} alt="Preview" style={styles.previewImg} />
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.btn, ...styles.btnSecondary }} onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary }}>
                  {editingLocation ? 'Update' : 'Create'} Location
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLocations;
